import React, { useEffect, useRef, useState } from "react";
import BorderCard from "components/common/BorderCard";
import healthIcon from "assets/icons/health-icon.png";
import ReactSelect from "react-select";
import infoIcon from "assets/icons/info-icon.svg";
import imgIcon from "assets/icons/img-icon.svg";
import pdfIcon from "assets/icons/pdf-icon.svg";
import { useNavigate } from "react-router-dom";
import SingleButtonFooter from "components/common/SingleButtonFooter";
import { customStyles, policyTypesConstant } from "config/constants";
import { useAppDispatch, useAppSelector, useUserDetails } from "utils/hooks";
import { getAllCompaniesThunk } from "store/thunk/main.thunk";
import BottomContent from "components/common/BottomContent";
import { uploadPolicyDocThunk } from "store/thunk/hcr.thunk";
import { clearData, saveDischargeList, saveDocList, uploadFormValuesHandler, saveMedicalBillsList, saveBankDetailsList, savePanCardList, saveOtherDocsList } from "store/slice/hcr.slice";
import { toast } from "react-toastify";
import deleteIcon from "assets/icons/delete-icon-red.svg";
import imageCompression from "browser-image-compression";
import jsPDF from "jspdf";
import UploadSuccessModal from "./components/UploadModal";

const UploadPolicy = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const fileInputRef: any = useRef();
  const fileDisInputRef: any = useRef();
  const fileHospitalInputRef: any = useRef();
  const filePanInputRef: any = useRef();
  const fileBankInputRef: any = useRef();
  const fileOtherInputRef: any = useRef();
  const currentUserDetails = useUserDetails();
  const docList = useAppSelector((state) => state.rootReducer.hcrSlice.docList);
  const dischargeList = useAppSelector((state) => state.rootReducer.hcrSlice.dischargeList);
  const medicalBillsList = useAppSelector((state) => state.rootReducer.hcrSlice.medicalBillsList || []);
  const panCardList = useAppSelector((state) => state.rootReducer.hcrSlice.panCardList || []);
  const bankDetailsList = useAppSelector((state) => state.rootReducer.hcrSlice.bankDetailsList || []);
  const otherDocsList = useAppSelector((state) => state.rootReducer.hcrSlice.otherDocsList || []);
  const companiesList = useAppSelector((state) => state.rootReducer.mainSlice.companyList);
  const formValues = useAppSelector((state) => state.rootReducer.hcrSlice.uploadPolicyFormValues);
  const isLoading = useAppSelector((state) => state.rootReducer.hcrSlice.uploadDocLoading);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    dispatch(getAllCompaniesThunk());

    return () => {
      dispatch(clearData());
    };
  }, []);

  const submitHandler = async () => {
    const formData = new FormData();
    formData.append("typeOfPolicy", formValues.typeOfPolicy.value);
    formData.append("insuranceCompanyId", formValues.insuranceCompanyId._id);
    formData.append("owner", currentUserDetails._id);
    formData.append("phone", currentUserDetails.phone);
    formData.append("hcrSrc", "hcr-web");
    formData.append("policyTypeId", "3030b68f-82e9-4dfb-b2b8-82b743d0692a");
    formData.append("source", "claimfiling");

    if (currentUserDetails?.userCreatedBy) formData.append("partnerId", currentUserDetails?.userCreatedBy);

    // Helper to process a list of files (images merged to PDF, existing PDFs appended)
    const appendFilesToFormData = async (files: File[], fieldName: string, baseName: string) => {
      const pdfs = files.filter((f) => f.type === "application/pdf");
      const images = files.filter((f) => f.type !== "application/pdf");

      // Append existing PDFs
      pdfs.forEach((pdf, index) => {
        formData.append(fieldName, pdf, `${new Date().getTime()}_${baseName}_${index}.pdf`);
      });

      // Merge images to PDF and append
      if (images.length > 0) {
        const mergedImagesPdf = await combineImagesToPdf(images);
        formData.append(fieldName, mergedImagesPdf, `${new Date().getTime()}_${baseName}_images.pdf`);
      }
    };

    if (docList.length > 0) await appendFilesToFormData(docList, "policy", "policy");
    if (dischargeList.length > 0) await appendFilesToFormData(dischargeList, "discharge", "discharge");
    if (medicalBillsList.length > 0) await appendFilesToFormData(medicalBillsList, "hospitalBillDoc", "hospital-medical_bills");
    if (panCardList.length > 0) await appendFilesToFormData(panCardList, "panDoc", "pan");
    if (bankDetailsList.length > 0) await appendFilesToFormData(bankDetailsList, "bankDoc", "bank");
    if (otherDocsList.length > 0) await appendFilesToFormData(otherDocsList, "otherDoc", "other");

    const payload = {
      query: {
        userId: currentUserDetails?._id,
        name: "Self",
        phone: currentUserDetails?.phone,
        policyType: "Health"
      },
      body: formData
    };

    dispatch(uploadPolicyDocThunk(payload))
      .unwrap()
      .then((data) => {
        if (data?.success && data?.data?._id) {
          if (data?.message === "Thank you for submitting the policy details") {
            setOpen(true);
          } else navigate("/hcr/reimbursement/" + data?.data?._id);
        }
      });
  };
  const combineImagesToPdf = async (documentList?: File[]): Promise<Blob> => {
    const doc = new jsPDF();
    const compressedFiles: File[] = [];

    const options = {
      maxSizeMB: 10,
      maxWidthOrHeight: 1920,
      useWebWorker: true
    };

    for (const file of documentList || []) {
      const fileType = file.name.split(".").pop()?.toLowerCase();
      if (fileType === "jpg" || fileType === "jpeg" || fileType === "png") {
        try {
          const compressedFile = await imageCompression(file, options);
          compressedFiles.push(compressedFile);
        } catch (error) {
          console.error("Image compression error:", error);
        }
      }
    }

    for (const [index, file] of compressedFiles.entries()) {
      const imgData = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(file);
      });

      const img = new Image();
      img.src = imgData;

      await new Promise<void>((resolve) => {
        img.onload = () => {
          const imgWidth = img.width;
          const imgHeight = img.height;
          const pageWidth = doc.internal.pageSize.getWidth();
          const ratio = pageWidth / imgWidth;
          const scaledWidth = pageWidth;
          const scaledHeight = imgHeight * ratio;
          const yPos = (doc.internal.pageSize.getHeight() - scaledHeight) / 2;

          if (index > 0) doc.addPage();
          doc.addImage(imgData, "JPEG", 0, yPos, scaledWidth, scaledHeight);
          resolve();
        };
      });
    }

    const pdfBlob = doc.output("blob");

    return pdfBlob;
  };

  // const generateDynamicFileName = (docName: string): string => {
  //   const now = dayjs();

  //   const month = now.format("MMM");
  //   const day = now.format("DD");
  //   const year = now.format("YYYY");
  //   const time = now.format("HHmm");

  //   return `${month}${day}${year}${time}_${docName}.pdf`;
  // };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []).slice(0, 1);
    if (!validateFiles(files)) return;
    dispatch(saveDocList(files));
  };

  const handleDisFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []).slice(0, 1);
    if (!validateFiles(files)) return;
    dispatch(saveDischargeList(files));
  };

  const validateFiles = (files: File[]) => {
    const maxSize = 10 * 1024 * 1024;
    const maxFiles = 10;
    const exceededFiles = files.filter((file) => file.size > maxSize);

    if (exceededFiles.length > 0) {
      toast.error("One or more files exceed the 10MB limit.");
      return false;
    }
    if (files.length > maxFiles) {
      toast.error(`You can only upload a maximum of ${maxFiles} files.`);
      return false;
    }
    return true;
  };

  const handleHospitalFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (!validateFiles([...medicalBillsList, ...files])) return;
    dispatch(saveMedicalBillsList([...medicalBillsList, ...files]));
  };

  const handlePanFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []).slice(0, 1);
    if (!validateFiles(files)) return;
    dispatch(savePanCardList(files));
  };

  const handleBankFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []).slice(0, 1);
    if (!validateFiles(files)) return;
    dispatch(saveBankDetailsList(files));
  };

  const handleOtherFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []).slice(0, 1);
    if (!validateFiles(files)) return;
    dispatch(saveOtherDocsList(files));
  };

  return (
    <>
      <BorderCard className="w-[90%] md:w-[70%] lg:w-[50%]">
        <div className="pt-0 md:pt-6 pb-5 md:pb-8 flex flex-col ">
          <p className="font-extrabold hidden md:block text-2xl px-4 md:px-7">Upload Your Policy</p>
          <div className="flex flex-col md:flex-row w-full mt-5 px-4 md:px-7">
            <div className="flex-[30%] flex flex-col items-start gap-2 justify-start">
              <p className="font-bold text-sm">Insurance Type</p>
              <img src={healthIcon} alt="health-icon" className="w-12 h-12" />
              <p className="font-medium text-xs text-center pl-1">Health</p>
            </div>
            <div className="flex-[70%] space-y-2 border-none md:border-l-[1px] pl-0 pt-4 md:pt-0  md:pl-7 border-[#00000033] h-48 flex flex-col items-start justify-between">
              <p className="font-bold text-sm">Insurance Company *</p>
              <ReactSelect value={formValues.insuranceCompanyId} onChange={(option: any) => dispatch(uploadFormValuesHandler({ name: "insuranceCompanyId", value: option }))} options={companiesList} getOptionLabel={(option) => option.name} getOptionValue={(option: any) => option._id} styles={customStyles} placeholder="Insurance Company" className="w-full" />
              <div className="flex gap-2 items-start">
                <img src={infoIcon} alt="info icon" className="mt-1" />
                <p className="text-xs text-[#000000B2] font-medium">Please note If you are unable to find your insurance company, it means that this feature will be available for your insurance company soon.</p>
              </div>
              <p className="font-bold text-sm">Policy Type *</p>
              <ReactSelect value={formValues.typeOfPolicy} onChange={(option: any) => dispatch(uploadFormValuesHandler({ name: "typeOfPolicy", value: option }))} options={policyTypesConstant} styles={customStyles} placeholder="Policy Type" isSearchable={false} className="w-full" />
            </div>
          </div>
          <div className="hidden md:flex items-center justify-between bg-[#F9F9F9] my-3 py-3 px-6 rounded-md mt-5">
            <div className="font-bold text-base whitespace-nowrap">Upload Policy</div>
            <div className="flex items-center gap-6">
              {docList.length > 0 && (
                <div className="flex flex-col gap-2">
                  {docList.map((file, idx) => (
                    <p key={idx} className="text-xs items-center flex justify-between gap-4 bg-uploadTheme text-gray-600 rounded px-2 py-1">
                      <span>{file.name.length > 20 ? file.name.substring(0, 20) + "..." : file.name}</span>
                      <img
                        src={deleteIcon}
                        alt="delete-icon"
                        className="w-4 h-4 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          const newList = [...docList];
                          newList.splice(idx, 1);
                          dispatch(saveDocList(newList));
                        }}
                      />
                    </p>
                  ))}
                </div>
              )}
              <div className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div onClick={() => { if (docList.some((f: any) => f.type === 'application/pdf')) return; navigate("docs/image", { state: "policy" }); }} className={`w-10 h-10  bg-white p-2 flex justify-center items-center rounded-md shadow-shadowAll ${docList.some((f: any) => f.type === 'application/pdf') ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                    <img src={imgIcon} alt="img-icon" />
                  </div>
                  <p className="font-medium text-xs text-center pt-1">Image</p>
                </div>
                <div className="flex flex-col items-center">
                  <div onClick={() => fileInputRef.current?.click()} className="w-10 h-10 p-2 cursor-pointer bg-white flex items-center justify-center rounded-md shadow-shadowAll">
                    <img src={pdfIcon} alt="img-icon" />
                  </div>
                  <p className="font-medium text-xs text-center pt-1">PDF</p>
                  <input type="file" accept={".pdf"} ref={fileInputRef} onChange={handleFileChange} className="hidden" />
                </div>
              </div>
            </div>
          </div>
          <div className="hidden md:flex items-center justify-between bg-[#F9F9F9] my-3 py-3 px-6 rounded-md">
            <div className="font-bold text-base whitespace-nowrap">Upload Discharge Summary</div>
            <div className="flex items-center gap-6">
              {dischargeList.length > 0 && (
                <div className="flex flex-col gap-2">
                  {dischargeList.map((file, idx) => (
                    <p key={idx} className="text-xs items-center flex justify-between gap-4 bg-uploadTheme text-gray-600 rounded px-2 py-1">
                      <span>{file.name.length > 20 ? file.name.substring(0, 20) + "..." : file.name}</span>
                      <img
                        src={deleteIcon}
                        alt="delete-icon"
                        className="w-4 h-4 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          const newList = [...dischargeList];
                          newList.splice(idx, 1);
                          dispatch(saveDischargeList(newList));
                        }}
                      />
                    </p>
                  ))}
                </div>
              )}
              <div className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div onClick={() => { if (dischargeList.some((f: any) => f.type === 'application/pdf')) return; navigate("docs/image", { state: "discharge" }); }} className={`w-10 h-10  bg-white p-2 flex justify-center items-center rounded-md shadow-shadowAll ${dischargeList.some((f: any) => f.type === 'application/pdf') ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                    <img src={imgIcon} alt="img-icon" />
                  </div>
                  <p className="font-medium text-xs text-center pt-1">Image</p>
                </div>
                <div className="flex flex-col items-center">
                  <div onClick={() => fileDisInputRef.current?.click()} className="w-10 h-10 p-2 cursor-pointer bg-white flex items-center justify-center rounded-md shadow-shadowAll">
                    <img src={pdfIcon} alt="img-icon" />
                  </div>
                  <p className="font-medium text-xs text-center pt-1">PDF</p>
                  <input type="file" accept={".pdf"} ref={fileDisInputRef} onChange={handleDisFileChange} className="hidden" />
                </div>
              </div>
            </div>
          </div>
          <div className="hidden md:flex items-center justify-between bg-[#F9F9F9] my-3 py-3 px-6 rounded-md">
            <div className="font-bold text-base">Upload Hospital/Medical Bills</div>
            <div className="flex items-center gap-6">
              {medicalBillsList.length > 0 && (
                <div className="flex flex-col gap-2">
                  {medicalBillsList.map((file, idx) => (
                    <p key={idx} className="text-xs items-center flex justify-between gap-4 bg-uploadTheme text-gray-600 rounded px-2 py-1">
                      <span>{file.name.length > 20 ? file.name.substring(0, 20) + "..." : file.name}</span>
                      <img
                        src={deleteIcon}
                        alt="delete-icon"
                        className="w-4 h-4 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          const newList = [...medicalBillsList];
                          newList.splice(idx, 1);
                          dispatch(saveMedicalBillsList(newList));
                        }}
                      />
                    </p>
                  ))}
                </div>
              )}
              <div className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div onClick={() => { if (medicalBillsList.some((f: any) => f.type === 'application/pdf')) return; navigate("docs/image", { state: "hospital" }); }} className={`w-10 h-10  bg-white p-2 flex justify-center items-center rounded-md shadow-shadowAll ${medicalBillsList.some((f: any) => f.type === 'application/pdf') ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                    <img src={imgIcon} alt="img-icon" />
                  </div>
                  <p className="font-medium text-xs text-center pt-1">Image</p>
                </div>
                <div className="flex flex-col items-center">
                  <div onClick={() => fileHospitalInputRef.current?.click()} className="w-10 h-10 p-2 cursor-pointer bg-white flex items-center justify-center rounded-md shadow-shadowAll">
                    <img src={pdfIcon} alt="img-icon" />
                  </div>
                  <p className="font-medium text-xs text-center pt-1">PDF</p>
                  <input type="file" accept={".pdf"} ref={fileHospitalInputRef} onChange={handleHospitalFileChange} className="hidden" multiple />
                </div>
              </div>
            </div>
          </div>

          <div className="hidden md:flex items-center justify-between bg-[#F9F9F9] my-3 py-3 px-6 rounded-md">
            <div className="font-bold text-base whitespace-nowrap">Upload PAN Card</div>
            <div className="flex items-center gap-6">
              {panCardList.length > 0 && (
                <div className="flex flex-col gap-2">
                  {panCardList.map((file, idx) => (
                    <p key={idx} className="text-xs items-center flex justify-between gap-4 bg-uploadTheme text-gray-600 rounded px-2 py-1">
                      <span>{file.name.length > 20 ? file.name.substring(0, 20) + "..." : file.name}</span>
                      <img
                        src={deleteIcon}
                        alt="delete-icon"
                        className="w-4 h-4 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          const newList = [...panCardList];
                          newList.splice(idx, 1);
                          dispatch(savePanCardList(newList));
                        }}
                      />
                    </p>
                  ))}
                </div>
              )}
              <div className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div onClick={() => { if (panCardList.some((f: any) => f.type === 'application/pdf')) return; navigate("docs/image", { state: "pan" }); }} className={`w-10 h-10  bg-white p-2 flex justify-center items-center rounded-md shadow-shadowAll ${panCardList.some((f: any) => f.type === 'application/pdf') ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                    <img src={imgIcon} alt="img-icon" />
                  </div>
                  <p className="font-medium text-xs text-center pt-1">Image</p>
                </div>
                <div className="flex flex-col items-center">
                  <div onClick={() => filePanInputRef.current?.click()} className="w-10 h-10 p-2 cursor-pointer bg-white flex items-center justify-center rounded-md shadow-shadowAll">
                    <img src={pdfIcon} alt="img-icon" />
                  </div>
                  <p className="font-medium text-xs text-center pt-1">PDF</p>
                  <input type="file" accept={".pdf"} ref={filePanInputRef} onChange={handlePanFileChange} className="hidden" />
                </div>
              </div>
            </div>
          </div>

          <div className="hidden md:flex items-center justify-between bg-[#F9F9F9] my-3 py-3 px-6 rounded-md">
            <div className="font-bold text-base whitespace-nowrap">Upload Bank Details</div>
            <div className="flex items-center gap-6">
              {bankDetailsList.length > 0 && (
                <div className="flex flex-col gap-2">
                  {bankDetailsList.map((file, idx) => (
                    <p key={idx} className="text-xs items-center flex justify-between gap-4 bg-uploadTheme text-gray-600 rounded px-2 py-1">
                      <span>{file.name.length > 20 ? file.name.substring(0, 20) + "..." : file.name}</span>
                      <img
                        src={deleteIcon}
                        alt="delete-icon"
                        className="w-4 h-4 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          const newList = [...bankDetailsList];
                          newList.splice(idx, 1);
                          dispatch(saveBankDetailsList(newList));
                        }}
                      />
                    </p>
                  ))}
                </div>
              )}
              <div className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div onClick={() => { if (bankDetailsList.some((f: any) => f.type === 'application/pdf')) return; navigate("docs/image", { state: "bank" }); }} className={`w-10 h-10  bg-white p-2 flex justify-center items-center rounded-md shadow-shadowAll ${bankDetailsList.some((f: any) => f.type === 'application/pdf') ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                    <img src={imgIcon} alt="img-icon" />
                  </div>
                  <p className="font-medium text-xs text-center pt-1">Image</p>
                </div>
                <div className="flex flex-col items-center">
                  <div onClick={() => fileBankInputRef.current?.click()} className="w-10 h-10 p-2 cursor-pointer bg-white flex items-center justify-center rounded-md shadow-shadowAll">
                    <img src={pdfIcon} alt="img-icon" />
                  </div>
                  <p className="font-medium text-xs text-center pt-1">PDF</p>
                  <input type="file" accept={".pdf"} ref={fileBankInputRef} onChange={handleBankFileChange} className="hidden" />
                </div>
              </div>
            </div>
          </div>
          <div className="hidden md:flex items-center justify-between bg-[#F9F9F9] my-3 py-3 px-6 rounded-md">
            <div className="font-bold text-base whitespace-nowrap">Upload Other Documents</div>
            <div className="flex items-center gap-6">
              {otherDocsList.length > 0 && (
                <div className="flex flex-col gap-2">
                  {otherDocsList.map((file, idx) => (
                    <p key={idx} className="text-xs items-center flex justify-between gap-4 bg-uploadTheme text-gray-600 rounded px-2 py-1">
                      <span>{file.name.length > 20 ? file.name.substring(0, 20) + "..." : file.name}</span>
                      <img
                        src={deleteIcon}
                        alt="delete-icon"
                        className="w-4 h-4 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          const newList = [...otherDocsList];
                          newList.splice(idx, 1);
                          dispatch(saveOtherDocsList(newList));
                        }}
                      />
                    </p>
                  ))}
                </div>
              )}
              <div className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div onClick={() => { if (otherDocsList.some((f: any) => f.type === 'application/pdf')) return; navigate("docs/image", { state: "other" }); }} className={`w-10 h-10  bg-white p-2 flex justify-center items-center rounded-md shadow-shadowAll ${otherDocsList.some((f: any) => f.type === 'application/pdf') ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                    <img src={imgIcon} alt="img-icon" />
                  </div>
                  <p className="font-medium text-xs text-center pt-1">Image</p>
                </div>
                <div className="flex flex-col items-center">
                  <div onClick={() => fileOtherInputRef.current?.click()} className="w-10 h-10 p-2 cursor-pointer bg-white flex items-center justify-center rounded-md shadow-shadowAll">
                    <img src={pdfIcon} alt="img-icon" />
                  </div>
                  <p className="font-medium text-xs text-center pt-1">PDF</p>
                  <input type="file" accept={".pdf"} ref={fileOtherInputRef} onChange={handleOtherFileChange} className="hidden" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <SingleButtonFooter className={`${docList.length === 0 || isLoading || formValues.insuranceCompanyId?._id == "" || formValues.typeOfPolicy?.value == "" ? "opacity-50 pointer-events-none" : ""}`} isLoading={isLoading} onClick={submitHandler} buttonTitle="NEXT" view="web" />
      </BorderCard>

      
      <BorderCard className="w-[90%] md:w-[70%] lg:w-[60%] my-3 block md:hidden overflow-hidden bg-[#F9F9F9]">
        <p className="py-3 px-5 font-extrabold">Upload Policy </p>
        <div className="bg-white shadow p-4">
          <div className="flex gap-4">
            <div>
              <div onClick={() => { if (docList.some((f: any) => f.type === 'application/pdf')) return; navigate("docs/image", { state: "policy" }); }} className={`w-8 h-8  bg-white p-2 flex justify-center items-center rounded-md shadow-shadowAll ${docList.some((f: any) => f.type === 'application/pdf') ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                <img src={imgIcon} alt="img-icon" />
              </div>
              <p className="font-medium text-xs text-center pl-1 pt-2">Image</p>
            </div>
            <div>
              <div onClick={() => fileInputRef.current?.click()} className="w-8 h-8 p-2 cursor-pointer bg-white flex items-center justify-center rounded-md shadow-shadowAll">
                <img src={pdfIcon} alt="img-icon" />
              </div>
              <p className="font-medium text-xs text-center pl-1 pt-2">PDF</p>
              <input type="file" accept={".pdf"} ref={fileInputRef} onChange={handleFileChange} className="hidden" />
            </div>
          </div>
          {docList.length > 0 && (
            <div className="flex flex-col gap-2 mt-4">
              {docList.map((file, idx) => (
                <p key={idx} className="text-xs items-center flex justify-between gap-4 bg-uploadTheme text-gray-600 rounded px-2 py-1">
                  <span>{file.name.length > 20 ? file.name.substring(0, 20) + "..." : file.name}</span>
                  <img
                    src={deleteIcon}
                    alt="delete-icon"
                    className="w-4 h-4 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      const newList = [...docList];
                      newList.splice(idx, 1);
                      dispatch(saveDocList(newList));
                    }}
                  />
                </p>
              ))}
            </div>
          )}
        </div>
      </BorderCard>
      <BorderCard className="w-[90%] md:w-[70%] lg:w-[60%] my-3 block md:hidden overflow-hidden bg-[#F9F9F9]">
        <p className="py-3 px-5 font-extrabold">Upload Discharge Summary </p>
        <div className="bg-white shadow p-4">
          <div className="flex gap-4">
            <div>
              <div onClick={() => { if (dischargeList.some((f: any) => f.type === 'application/pdf')) return; navigate("docs/image", { state: "discharge" }); }} className={`w-8 h-8  bg-white p-2 flex justify-center items-center rounded-md shadow-shadowAll ${dischargeList.some((f: any) => f.type === 'application/pdf') ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                <img src={imgIcon} alt="img-icon" />
              </div>
              <p className="font-medium text-xs text-center pl-1 pt-2">Image</p>
            </div>
            <div>
              <div onClick={() => fileDisInputRef.current?.click()} className="w-8 h-8 p-2 cursor-pointer bg-white flex items-center justify-center rounded-md shadow-shadowAll">
                <img src={pdfIcon} alt="img-icon" />
              </div>
              <p className="font-medium text-xs text-center pl-1 pt-2">PDF</p>
              <input type="file" accept={".pdf"} ref={fileDisInputRef} onChange={handleDisFileChange} className="hidden" />
            </div>
          </div>
          {dischargeList.length > 0 && (
            <div className="flex flex-col gap-2 mt-4">
              {dischargeList.map((file, idx) => (
                <p key={idx} className="text-xs items-center flex justify-between gap-4 bg-uploadTheme text-gray-600 rounded px-2 py-1">
                  <span>{file.name.length > 20 ? file.name.substring(0, 20) + "..." : file.name}</span>
                  <img
                    src={deleteIcon}
                    alt="delete-icon"
                    className="w-4 h-4 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      const newList = [...dischargeList];
                      newList.splice(idx, 1);
                      dispatch(saveDischargeList(newList));
                    }}
                  />
                </p>
              ))}
            </div>
          )}
        </div>
      </BorderCard>
      <BorderCard className="w-[90%] md:w-[70%] lg:w-[60%] my-3 block md:hidden overflow-hidden bg-[#F9F9F9]">
        <p className="py-3 px-5 font-extrabold">Upload Hospital/Medical Bills </p>
        <div className="bg-white shadow p-4">
          <div className="flex gap-4">
            <div>
              <div onClick={() => { if (medicalBillsList.some((f: any) => f.type === 'application/pdf')) return; navigate("docs/image", { state: "hospital" }); }} className={`w-8 h-8  bg-white p-2 flex justify-center items-center rounded-md shadow-shadowAll ${medicalBillsList.some((f: any) => f.type === 'application/pdf') ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                <img src={imgIcon} alt="img-icon" />
              </div>
              <p className="font-medium text-xs text-center pl-1 pt-2">Image</p>
            </div>
            <div>
              <div onClick={() => fileHospitalInputRef.current?.click()} className="w-8 h-8 p-2 cursor-pointer bg-white flex items-center justify-center rounded-md shadow-shadowAll">
                <img src={pdfIcon} alt="img-icon" />
              </div>
              <p className="font-medium text-xs text-center pl-1 pt-2">PDF</p>
              <input type="file" accept={".pdf"} ref={fileHospitalInputRef} onChange={handleHospitalFileChange} className="hidden" multiple />
            </div>
          </div>
          {medicalBillsList.length > 0 && (
            <div className="flex flex-col gap-2 mt-4">
              {medicalBillsList.map((file, idx) => (
                <p key={idx} className="text-xs items-center flex justify-between gap-4 bg-uploadTheme text-gray-600 rounded px-2 py-1">
                  <span>{file.name.length > 20 ? file.name.substring(0, 20) + "..." : file.name}</span>
                  <img
                    src={deleteIcon}
                    alt="delete-icon"
                    className="w-4 h-4 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      const newList = [...medicalBillsList];
                      newList.splice(idx, 1);
                      dispatch(saveMedicalBillsList(newList));
                    }}
                  />
                </p>
              ))}
            </div>
          )}
        </div>
      </BorderCard>
      <BorderCard className="w-[90%] md:w-[70%] lg:w-[60%] my-3 block md:hidden overflow-hidden bg-[#F9F9F9]">
        <p className="py-3 px-5 font-extrabold">Upload PAN Card </p>
        <div className="bg-white shadow p-4">
          <div className="flex gap-4">
            <div>
              <div onClick={() => { if (panCardList.some((f: any) => f.type === 'application/pdf')) return; navigate("docs/image", { state: "pan" }); }} className={`w-8 h-8  bg-white p-2 flex justify-center items-center rounded-md shadow-shadowAll ${panCardList.some((f: any) => f.type === 'application/pdf') ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                <img src={imgIcon} alt="img-icon" />
              </div>
              <p className="font-medium text-xs text-center pl-1 pt-2">Image</p>
            </div>
            <div>
              <div onClick={() => filePanInputRef.current?.click()} className="w-8 h-8 p-2 cursor-pointer bg-white flex items-center justify-center rounded-md shadow-shadowAll">
                <img src={pdfIcon} alt="img-icon" />
              </div>
              <p className="font-medium text-xs text-center pl-1 pt-2">PDF</p>
              <input type="file" accept={".pdf"} ref={filePanInputRef} onChange={handlePanFileChange} className="hidden" />
            </div>
          </div>
          {panCardList.length > 0 && (
            <div className="flex flex-col gap-2 mt-4">
              {panCardList.map((file, idx) => (
                <p key={idx} className="text-xs items-center flex justify-between gap-4 bg-uploadTheme text-gray-600 rounded px-2 py-1">
                  <span>{file.name.length > 20 ? file.name.substring(0, 20) + "..." : file.name}</span>
                  <img
                    src={deleteIcon}
                    alt="delete-icon"
                    className="w-4 h-4 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      const newList = [...panCardList];
                      newList.splice(idx, 1);
                      dispatch(savePanCardList(newList));
                    }}
                  />
                </p>
              ))}
            </div>
          )}
        </div>
      </BorderCard>
      <BorderCard className="w-[90%] md:w-[70%] lg:w-[60%] my-3 block md:hidden overflow-hidden bg-[#F9F9F9]">
        <p className="py-3 px-5 font-extrabold">Upload Bank Details </p>
        <div className="bg-white shadow p-4">
          <div className="flex gap-4">
            <div>
              <div onClick={() => { if (bankDetailsList.some((f: any) => f.type === 'application/pdf')) return; navigate("docs/image", { state: "bank" }); }} className={`w-8 h-8  bg-white p-2 flex justify-center items-center rounded-md shadow-shadowAll ${bankDetailsList.some((f: any) => f.type === 'application/pdf') ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                <img src={imgIcon} alt="img-icon" />
              </div>
              <p className="font-medium text-xs text-center pl-1 pt-2">Image</p>
            </div>
            <div>
              <div onClick={() => fileBankInputRef.current?.click()} className="w-8 h-8 p-2 cursor-pointer bg-white flex items-center justify-center rounded-md shadow-shadowAll">
                <img src={pdfIcon} alt="img-icon" />
              </div>
              <p className="font-medium text-xs text-center pl-1 pt-2">PDF</p>
              <input type="file" accept={".pdf"} ref={fileBankInputRef} onChange={handleBankFileChange} className="hidden" />
            </div>
          </div>
          {bankDetailsList.length > 0 && (
            <div className="flex flex-col gap-2 mt-4">
              {bankDetailsList.map((file, idx) => (
                <p key={idx} className="text-xs items-center flex justify-between gap-4 bg-uploadTheme text-gray-600 rounded px-2 py-1">
                  <span>{file.name.length > 20 ? file.name.substring(0, 20) + "..." : file.name}</span>
                  <img
                    src={deleteIcon}
                    alt="delete-icon"
                    className="w-4 h-4 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      const newList = [...bankDetailsList];
                      newList.splice(idx, 1);
                      dispatch(saveBankDetailsList(newList));
                    }}
                  />
                </p>
              ))}
            </div>
          )}
        </div>
      </BorderCard>
      <BorderCard className="w-[90%] md:w-[70%] lg:w-[60%] mb-20 block md:hidden overflow-hidden bg-[#F9F9F9]">
        <p className="py-3 px-5 font-extrabold">Upload Other Documents </p>
        <div className="bg-white shadow p-4">
          <div className="flex gap-4">
            <div>
              <div onClick={() => { if (otherDocsList.some((f: any) => f.type === 'application/pdf')) return; navigate("docs/image", { state: "other" }); }} className={`w-8 h-8  bg-white p-2 flex justify-center items-center rounded-md shadow-shadowAll ${otherDocsList.some((f: any) => f.type === 'application/pdf') ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                <img src={imgIcon} alt="img-icon" />
              </div>
              <p className="font-medium text-xs text-center pl-1 pt-2">Image</p>
            </div>
            <div>
              <div onClick={() => fileOtherInputRef.current?.click()} className="w-8 h-8 p-2 cursor-pointer bg-white flex items-center justify-center rounded-md shadow-shadowAll">
                <img src={pdfIcon} alt="img-icon" />
              </div>
              <p className="font-medium text-xs text-center pl-1 pt-2">PDF</p>
              <input type="file" accept={".pdf"} ref={fileOtherInputRef} onChange={handleOtherFileChange} className="hidden" />
            </div>
          </div>
          {otherDocsList.length > 0 && (
            <div className="flex flex-col gap-2 mt-4">
              {otherDocsList.map((file, idx) => (
                <p key={idx} className="text-xs items-center flex justify-between gap-4 bg-uploadTheme text-gray-600 rounded px-2 py-1">
                  <span>{file.name.length > 20 ? file.name.substring(0, 20) + "..." : file.name}</span>
                  <img
                    src={deleteIcon}
                    alt="delete-icon"
                    className="w-4 h-4 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      const newList = [...otherDocsList];
                      newList.splice(idx, 1);
                      dispatch(saveOtherDocsList(newList));
                    }}
                  />
                </p>
              ))}
            </div>
          )}
        </div>
      </BorderCard>
      {open && (
        <UploadSuccessModal
          isOpen={open}
          onClose={() => setOpen(false)}
          onProceed={() => {
            setOpen(false);
            navigate("/hcr/all-claim-form");
          }}
        />
      )}
      <BottomContent>
        <SingleButtonFooter onClick={submitHandler} buttonTitle="NEXT" view="mobile" className={`${docList.length === 0 || isLoading || !formValues.insuranceCompanyId?._id || !formValues.typeOfPolicy?.value ? "opacity-50 pointer-events-none" : ""}`} isLoading={isLoading} />
      </BottomContent>
    </>
  );
};

export default UploadPolicy;
